import { useEffect, useRef } from 'react';
import { dataSource } from '@/data';
import type { BuildDetalhe, BuildResumo, FiltrosFeed } from '@/data/types';
import { useEventos } from './use-eventos';
import { usePaginaCursor, type ListaPaginada } from './use-pagina-cursor';

export type Feed = ListaPaginada<BuildResumo>;

/** Só os campos de `BuildResumo` — o detalhe traz manifesto e eventos que a lista não guarda. */
function paraResumo(detalhe: BuildDetalhe): Partial<BuildResumo> {
  const { artefatos, eventos, diff_anterior, elegivel_painel, incidentes, ...resumo } = detalhe;
  void artefatos;
  void eventos;
  void diff_anterior;
  void elegivel_painel;
  void incidentes;
  return resumo;
}

/**
 * Feed paginado por cursor (`next_cursor` do `GET /v1/builds`).
 *
 * Uma mudança de status feita no Sheet de detalhe chega pelo barramento e
 * atualiza o card no lugar — sem refazer a busca, para não perder a rolagem de
 * quem está homologando em sequência. O mesmo vale para incidentes: as versões
 * afetadas têm o resumo relido (contagem e severidade do selo) uma a uma.
 */
export function useFeed(filtros: FiltrosFeed): Feed {
  const lista = usePaginaCursor(filtros, (f, cursor, signal) => dataSource.listarBuilds(f, cursor, signal), (item) => item.id);
  const { definirItens } = lista;
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  useEventos((evento) => {
    if (evento.tipo === 'build') {
      definirItens((atuais) =>
        atuais.map((item) => (item.id === evento.build.id ? { ...item, ...paraResumo(evento.build) } : item)),
      );
    } else if (evento.tipo === 'painel') {
      definirItens((atuais) => atuais.map((item) => ({ ...item, no_painel: item.versao === evento.versao })));
    } else if (evento.tipo === 'incidente') {
      for (const versao of new Set(evento.versoes)) {
        void dataSource
          .obterBuild(versao)
          .then((detalhe) => {
            if (!vivo.current) return;
            definirItens((atuais) =>
              atuais.map((item) => (item.id === detalhe.id ? { ...item, ...paraResumo(detalhe) } : item)),
            );
          })
          .catch(() => {
            // Atualização de fundo: se falhar, o selo fica como estava até o próximo carregamento.
          });
      }
    }
  });

  return lista;
}
